import React from 'react';
import PropTypes from 'prop-types';
import {TextInput} from '@/components/atoms';
import PointsCondition from './PointsCondition';
import styles from '../RewardSetup.module.scss';
import {useAppSelector} from '@/store';
import {getCurrencySymbol} from '@/utils/commons';
import {preventInvalidNumericInput} from '@/utils/inputValidations';

/**
 * CashDiscountCondition component displays the CASH/DISCOUNT/PEP_VOUCHER_DISCOUNT reward condition UI
 * Uses single `value` field in formData (mapped to rewardCashAmount or discountAmount in API based on reward type)
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing value, redeemPoints
 * @param {Function} props.handleValueChange - Handler for value changes
 * @param {Function} props.handlePointsChange - Handler for points input changes
 * @param {string} props.valueError - Error message for value validation
 * @param {string} props.pointsError - Error message for points validation
 * @returns {JSX.Element} The rendered CashDiscountCondition component
 */
const CashDiscountCondition = ({
  formData,
  handleValueChange,
  handlePointsChange,
  valueError,
  pointsError
}) => {
  const countryCode = useAppSelector((state) => state.countryCode)?.countryCode;
  const currencySymbol = getCurrencySymbol(countryCode);

  return (
    <div className='flex flex-wrap gap-[13px]'>
      <span className='mt-2 whitespace-nowrap text-sm'>{currencySymbol}</span>
      <div className={`w-32 ${styles.rewardSetup__inputPoints}`}>
        <TextInput
          className='text-sm placeholder:text-sm'
          value={formData.value || ''}
          onChange={handleValueChange}
          label=''
          placeholderText='Value'
          handleKeyDown={preventInvalidNumericInput}
          error={valueError}
          isDark={true}
          type='number'
          id='reward-value'
        />
      </div>
      <PointsCondition
        formData={formData}
        handlePointsChange={handlePointsChange}
        pointsError={pointsError}
      />
    </div>
  );
};

CashDiscountCondition.propTypes = {
  formData: PropTypes.shape({
    value: PropTypes.string,
    redeemPoints: PropTypes.string
  }).isRequired,
  handleValueChange: PropTypes.func.isRequired,
  handlePointsChange: PropTypes.func.isRequired,
  valueError: PropTypes.string,
  pointsError: PropTypes.string
};

export default CashDiscountCondition;
